import React from 'react'
import { CCard, CCardBody, CCol, CRow, CFormCheck } from '@coreui/react-pro'
import {
  CLUB_SERVICE_PACKAGES,
  CLUB_TEAM_BANDS,
  LEAGUE_TEAM_BANDS,
  DURATION_OPTIONS,
  getClubBasePrice,
  getLeagueBasePrice,
  computePricing,
} from './gameSetupConfig'

const money = (value) => (value === null || value === undefined ? null : `$${value.toFixed(2)}`)

const StepPackagePricing = ({
  isClub,
  competitionCategory,
  applyDurationAdjustment,
  servicePackage,
  onServicePackageChange,
  teamBand,
  onTeamBandChange,
  duration,
  onDurationChange,
  pricing,
  reviewRequired,
  // Amount already paid toward the current plan (upgrade flow only). Any combination
  // that would price out at or below this is a downgrade/no-op, which isn't allowed -
  // those options are shown disabled instead of being pickable.
  minPurchasePrice = 0,
  // Current active team count (upgrade flow only). A band whose max is below this
  // can't fit the game's actual teams, so it's shown disabled instead of being
  // pickable - the admin has to remove teams first to downgrade into it.
  currentTeamCount = 0,
}) => {
  const teamBands = isClub ? CLUB_TEAM_BANDS : LEAGUE_TEAM_BANDS

  const priceFor = (pkg, band, dur) => {
    const basePrice = isClub
      ? getClubBasePrice(pkg, band)
      : getLeagueBasePrice(competitionCategory, band)
    return computePricing(basePrice, dur, applyDurationAdjustment).finalPrice
  }

  const isBelowMinPurchase = (finalPrice) =>
    minPurchasePrice > 0 && finalPrice !== null && finalPrice < minPurchasePrice

  const isBelowTeamCount = (band) =>
    currentTeamCount > 0 && band.max !== null && band.max < currentTeamCount

  const amountDue =
    pricing.finalPrice === null ? null : Math.max(0, pricing.finalPrice - minPurchasePrice)

  return (
    <div>
      {isClub && (
        <>
          <h5>How would you like to run your competition?</h5>
          <CRow className="g-3 mb-4">
            {CLUB_SERVICE_PACKAGES.map((pkg) => {
              const selected = servicePackage === pkg.value
              const disabled = isBelowMinPurchase(priceFor(pkg.value, teamBand, duration))
              return (
                <CCol xs={12} md={4} key={pkg.value}>
                  <CCard
                    onClick={() => !disabled && onServicePackageChange(pkg.value)}
                    className={selected ? 'border-success shadow-sm' : ''}
                    style={{
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      height: '100%',
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >
                    <CCardBody>
                      <div className="d-flex justify-content-between align-items-start">
                        <strong>{pkg.label}</strong>
                        {selected && <span className="text-success">&#10003;</span>}
                      </div>
                      <small className="text-medium-emphasis">{pkg.description}</small>
                      {disabled && (
                        <div>
                          <small className="text-medium-emphasis fst-italic">
                            Already covered by what you&apos;ve paid
                          </small>
                        </div>
                      )}
                    </CCardBody>
                  </CCard>
                </CCol>
              )
            })}
          </CRow>
        </>
      )}

      {!isClub && (
        <CCard className="mb-4 border-success">
          <CCardBody>
            <strong>Season Package</strong>
            <p className="mb-0 text-medium-emphasis">
              MyClubtap manages setup, scoring, rankings and participant support throughout the
              competition.
            </p>
          </CCardBody>
        </CCard>
      )}

      <h5>How many {isClub ? 'club ' : ''}teams will be included?</h5>
      <CRow className="g-2 mb-4">
        {teamBands.map((band) => {
          const selected = teamBand === band.value
          const belowTeamCount = isBelowTeamCount(band)
          const disabled =
            isBelowMinPurchase(priceFor(servicePackage, band.value, duration)) || belowTeamCount
          return (
            <CCol xs={6} sm={4} md={2} key={band.value}>
              <CCard
                onClick={() => !disabled && onTeamBandChange(band.value)}
                className={selected ? 'border-success shadow-sm' : ''}
                style={{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  textAlign: 'center',
                  opacity: disabled ? 0.5 : 1,
                }}
              >
                <CCardBody className="py-2">
                  <div>{band.label}</div>
                  {!isClub && (
                    <small className="text-medium-emphasis">
                      up to {band.fixtureAllowance} fixtures
                    </small>
                  )}
                  {belowTeamCount && (
                    <div>
                      <small className="text-medium-emphasis fst-italic">
                        You have {currentTeamCount} teams - remove teams to select this plan
                      </small>
                    </div>
                  )}
                </CCardBody>
              </CCard>
            </CCol>
          )
        })}
      </CRow>

      <h5>How long will your competition run?</h5>
      <CRow className="g-2 mb-4">
        {DURATION_OPTIONS.map((option) => {
          const disabled = isBelowMinPurchase(priceFor(servicePackage, teamBand, option.value))
          return (
            <CCol xs={12} sm={4} key={option.value}>
              <CFormCheck
                type="radio"
                name="duration"
                id={`duration-${option.value}`}
                checked={duration === option.value}
                disabled={disabled}
                onChange={() => onDurationChange(option.value)}
                label={
                  <span>
                    <strong>{option.label}</strong>
                    <br />
                    <small className="text-medium-emphasis">{option.sublabel}</small>
                  </span>
                }
              />
            </CCol>
          )
        })}
      </CRow>

      {!applyDurationAdjustment && (
        <p className="text-medium-emphasis">
          Competition duration does not affect your package price. It helps MyClubtap plan delivery.
        </p>
      )}

      <CCard className="bg-light">
        <CCardBody>
          <strong>Indicative price</strong>
          {pricing.finalPrice === null ? (
            reviewRequired ? (
              <p className="mb-0 mt-2">
                Pricing for this selection will be confirmed by our team after account creation.
              </p>
            ) : (
              <p className="mb-0 mt-2 text-medium-emphasis">
                Select a package, team size and duration to see the indicative price.
              </p>
            )
          ) : (
            <div className="mt-2">
              <div>Base package: {money(pricing.basePrice)}</div>
              {applyDurationAdjustment && (
                <div>Duration adjustment: {money(pricing.priceAdjustment)}</div>
              )}
              {minPurchasePrice > 0 && <div>Already paid: -{money(minPurchasePrice)}</div>}
              <h3 className="text-success mt-2">AUD {amountDue.toFixed(2)}</h3>
              <small className="text-medium-emphasis">
                {reviewRequired
                  ? 'Indicative price - final inclusions will be confirmed by our team after account creation.'
                  : minPurchasePrice > 0
                  ? 'Amount due now, after crediting what you have already paid.'
                  : 'Final inclusions can be reviewed before creating your account.'}
              </small>
            </div>
          )}
        </CCardBody>
      </CCard>
    </div>
  )
}

export default StepPackagePricing
